import React, { useState } from 'react';
import { Bot, Check, Zap, ArrowRight } from 'lucide-react';
import { PRICING_LINES, PRICING_ADDONS, annualMonthly, formatUsd } from '../utils/pricing';

export default function PricingPage() {
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'annual'

  const [lineId, setLineId] = useState('agenda'); // 'agenda' | 'pedidos'
  const line = PRICING_LINES.find((l) => l.id === lineId) || PRICING_LINES[0];
  const plans = line.plans.map((plan) => ({
    ...plan,
    color: plan.highlight ? 'border-seryi-green shadow-xl shadow-seryi-green/10' : 'border-white/10',
    buttonText: `Seleccionar ${plan.name}`
  }));

  return (
    <div className="min-h-screen bg-seryi-bg-dark text-seryi-text font-sans">
      {/* NAVBAR */}
      <nav className="border-b border-white/10 bg-seryi-bg-dark/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-seryi-green to-[#00cc7d] flex items-center justify-center shadow-lg shadow-seryi-green/20">
              <Bot className="text-seryi-bg-dark" size={24} />
            </div>
            <span className="text-xl font-black text-white tracking-tight">KEYLA<span className="text-seryi-green">CRM</span></span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/login" className="text-sm font-semibold text-white hover:text-seryi-green transition-colors">
              Iniciar Sesión
            </a>
            <a href="/signup" className="px-5 py-2.5 rounded-xl bg-seryi-green text-seryi-bg-dark font-extrabold text-sm hover:brightness-110 transition-all shadow-lg shadow-seryi-green/20">
              Empezar Gratis
            </a>
          </div>
        </div>
      </nav>

      {/* HEADER */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-seryi-green/10 border border-seryi-green/20 text-seryi-green text-xs font-extrabold uppercase tracking-wider mb-6">
          <Zap size={14} /> Planes y Precios Keyla CRM
        </div>
        <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight mb-4">
          Escala tu Negocio con <span className="text-seryi-green">Inteligencia Artificial</span>
        </h1>
        <p className="text-lg text-seryi-text max-w-2xl mx-auto mb-8">
          Elige tu línea y el plan que mejor se adapte al tamaño de tu negocio y al volumen de tus conversaciones. Precios en dólares (US$) por mes, sin cobro por mensaje.
        </p>

        {/* SELECTOR DE LÍNEA */}
        <div role="tablist" aria-label="Línea de producto" className="inline-flex flex-wrap justify-center gap-2 bg-white/5 p-1.5 rounded-2xl border border-white/10 mb-3">
          {PRICING_LINES.map((l) => (
            <button
              key={l.id}
              role="tab"
              aria-selected={lineId === l.id}
              onClick={() => setLineId(l.id)}
              className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${
                lineId === l.id ? 'bg-seryi-green text-seryi-bg-dark shadow-lg' : 'text-seryi-text hover:text-white'
              }`}
            >
              {l.name}
            </button>
          ))}
        </div>
        <p className="text-sm text-seryi-text mb-8">{line.audience}</p>

        {/* BILLING TOGGLE */}
        <div className="inline-flex items-center gap-4 bg-white/5 p-1.5 rounded-2xl border border-white/10 mb-12">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${
              billingCycle === 'monthly'
                ? 'bg-seryi-green text-seryi-bg-dark shadow-lg'
                : 'text-seryi-text hover:text-white'
            }`}
          >
            Facturación Mensual
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${
              billingCycle === 'annual'
                ? 'bg-seryi-green text-seryi-bg-dark shadow-lg'
                : 'text-seryi-text hover:text-white'
            }`}
          >
            Facturación Anual
            <span className="bg-seryi-bg-dark text-seryi-green px-2 py-0.5 rounded-md text-[10px] font-black uppercase">
              20% DTO
            </span>
          </button>
        </div>

        {/* PRICING CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white/5 rounded-3xl p-8 border ${plan.color} flex flex-col justify-between transition-all hover:translate-y-[-4px]`}
            >
              {plan.highlight && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-seryi-green to-[#00cc7d] text-seryi-bg-dark text-xs font-black px-4 py-1 rounded-full uppercase tracking-wider shadow-lg">
                  {plan.badge}
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-black text-white">{plan.name}</h3>
                  {!plan.highlight && (
                    <span className="text-xs font-bold text-seryi-text uppercase bg-white/5 px-2.5 py-1 rounded-lg">
                      {plan.badge}
                    </span>
                  )}
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl sm:text-5xl font-black text-white">
                      {formatUsd(billingCycle === 'monthly' ? plan.priceMonthly : annualMonthly(plan.priceMonthly))}
                    </span>
                    <span className="text-sm text-seryi-text">/ mes</span>
                  </div>
                  {billingCycle === 'annual' && (
                    <p className="text-xs text-seryi-green font-semibold mt-1">
                      Facturado anualmente • Ahorro del 20%
                    </p>
                  )}
                </div>

                {/* RESOURCE LIMITS */}
                <div className="bg-white/5 rounded-2xl p-4 mb-6 border border-white/5 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-seryi-text">IA:</span>
                    <span className="font-bold text-white text-right">{plan.conversations}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-seryi-text">Alcance:</span>
                    <span className="font-bold text-white text-right">{plan.scale}</span>
                  </div>
                </div>

                {/* INCLUDED FEATURES */}
                <div className="space-y-3 mb-8">
                  <p className="text-xs font-bold text-seryi-text uppercase tracking-wider">
                    ¿Qué incluye este plan?
                  </p>
                  {plan.features.map((feat, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-seryi-green/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check size={12} className="text-seryi-green" />
                      </div>
                      <span className="text-sm text-white">{feat}</span>
                    </div>
                  ))}
                  {plan.notIncluded.map((feat, idx) => (
                    <div key={idx} className="flex items-start gap-3 opacity-40">
                      <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs text-seryi-text">-</span>
                      </div>
                      <span className="text-sm text-seryi-text line-through">{feat}</span>
                    </div>
                  ))}
                </div>
              </div>

              <a
                href={`/signup?plan=${plan.id}&line=${line.id}`}
                className={`w-full py-4 rounded-2xl font-extrabold text-sm text-center transition-all flex items-center justify-center gap-2 ${
                  plan.highlight
                    ? 'bg-seryi-green text-seryi-bg-dark hover:brightness-110 shadow-lg shadow-seryi-green/20'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {plan.buttonText}
                <ArrowRight size={16} />
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* NOTA Y COMPLEMENTOS */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <p className="text-xs text-seryi-text text-center mb-10">
          Una conversación cuenta todos los mensajes con un mismo cliente dentro de 24 horas. En los planes con "uso justo" el tope es el indicado; si lo necesitas, puedes sumar paquetes de conversaciones.
        </p>
        <h2 className="text-xl font-black text-white text-center mb-6">Complementos</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PRICING_ADDONS.map((addon) => (
            <div key={addon.id} className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-white">{addon.name}</p>
                <p className="text-xs text-seryi-text">{addon.note}</p>
              </div>
              <span className="text-lg font-black text-seryi-green whitespace-nowrap">{formatUsd(addon.price)}<span className="text-xs text-seryi-text font-normal"> / mes</span></span>
            </div>
          ))}
        </div>
      </div>

      {/* FOOTER CALLOUT */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <div className="bg-gradient-to-r from-seryi-green/10 via-white/5 to-seryi-green/10 rounded-3xl p-8 sm:p-12 border border-seryi-green/20">
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-4">
            ¿Tienes dudas sobre qué plan necesitas?
          </h2>
          <p className="text-seryi-text mb-8">
            Habla directamente con Keyla en nuestro WhatsApp y recibe una asesoría personalizada para tu negocio.
          </p>
          <a
            href="https://wa.me/573000000000"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-white text-seryi-bg-dark font-black text-sm hover:bg-seryi-green transition-all shadow-xl"
          >
            Hablar con Asesoría IA
          </a>
        </div>
      </div>
    </div>
  );
}
